import { noop } from 'lodash-es';
import isEmpty from 'lodash-es/isEmpty';
import { createContext, useState, useContext, PropsWithChildren, useEffect } from 'react';
import {
  AccessControlSettingsContext,
  getAllDefaultAccessSettings,
  fetchWorkspaceAccessSettings,
  PermissionMap,
} from 'wisestep-api';

export const PermissionContext = createContext<AccessControlSettingsContext | null>(null);
export default function PermissionContextProvider(
  props: PropsWithChildren<AccessControlSettingsContext | null>
) {
  const [permissionData, setPermissionData] = useState<PermissionMap | null>(null);

  const isAuthorized = (moduleName: string, actionKey: string, roleList: string[]): boolean => {
    const modulePermission = permissionData && permissionData[moduleName];
    if (modulePermission) {
      const permis = (modulePermission?.permissions ?? []).filter((item) =>
        roleList.includes(item.role)
      );
      const hasAuth = permis.some((perItem) => {
        return Object.values(perItem.permissions).some((accessPermission) => {
          return accessPermission.some((item) => item.actionKey == actionKey && item.value);
        });
      });
      return hasAuth;
    } else {
      return false;
    }
  };

  useEffect(() => {
    const moduleMap = {
      CLIENT_LISTING: 'invoicingservice',
      LEAD: 'invoicingservice',
      INVOICING: 'invoicingservice',
      ENTITY_LISTING: 'invoicingservice',
      NATIVE_SEARCH: 'candidatesearch',
      CANDIDATE_BOARD: 'candidatesearch',
      PREMIUM_JOB: 'jobsearch',
      JOB_BOARD: 'jobsearch',
      ATS_ACCESS: '',
      VMS_CONFIGURATION: 'jobsearch',
      SMS_CONFIGURATION: 'communicationservice',
      USERS_ACCESS: '',
      TEAMS_ACCESS: '',
      ATS_CUSTOM: '',
      COMPOSABLE_DATA_ACCESS: 'wsreporting',
      ATTRIBUTES_VISIBILITY: '',
      CRM_JOB: 'invoicingservice',
    };
    if (isEmpty(permissionData)) {
      (async function () {
        try {
          const data = {};
          Object.keys(moduleMap).forEach(async (module) => {
            const workspaceAccessSettings = await fetchWorkspaceAccessSettings(
              moduleMap[module],
              module
            );
            if (workspaceAccessSettings.data.length > 0) {
              const workspacePermission: AccessControlSettingsContext = {
                permissions: workspaceAccessSettings.data,
              };
              data[module] = workspacePermission;
              setPermissionData({ ...data, [module]: workspacePermission });
            } else {
              const response = await getAllDefaultAccessSettings(moduleMap[module], module);
              const workspacePermission: AccessControlSettingsContext = {
                permissions: response.data ?? [],
              };
              data[module] = workspacePermission;
              setPermissionData({ ...data, [module]: workspacePermission });
            }
          });
        } catch {
          noop();
        }
      })();
    }
  }, [permissionData]);

  return (
    <PermissionContext.Provider
      value={{
        ...permissionData,
        setAccessControlSettingsContext: setPermissionData,
        isAuthorized: isAuthorized,
      }}
    >
      {props.children}
    </PermissionContext.Provider>
  );
}

export function usePermissionContext() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('useAppState must be used within the AuthContextProvider');
  }
  return context;
}
